import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  Download,
  FileText,
  Maximize2,
  Minus,
  Network,
  Plus,
  Printer,
  Users,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import { listDepartments, listEmployees, type EmployeeListItem } from '@/api/employees.api';
import { listRounds, listRoundScores } from '@/api/eos/peopleAnalyzer.api';
import {
  listSeats,
  setSeatHiring,
  unassignSeat,
  updateSeat,
  type SeatScenario,
  type SeatView,
} from '@/api/seats.api';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { AssignSeatModal } from '@/components/seats/AssignSeatModal';
import { SeatCard, NODE_HEIGHT, NODE_WIDTH, SEAT_DRAG_MIME } from '@/components/seats/SeatCard';
import { SeatDrawer } from '@/components/seats/SeatDrawer';
import { SeatEditorModal } from '@/components/seats/SeatEditorModal';
import { buildSeatTree, employeesWithoutSeat, type SeatTreeNode } from '@/lib/accountability/buildSeatTree';
import { layoutSeatTree, type SeatLayout } from '@/lib/accountability/seatLayout';
import { aggregateGwcByMembership, roleLabel, type SeatGwc } from '@/lib/accountability/seatMeta';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';

/**
 * Accountability Chart — Phase G (Stream F, ADR-028).
 *
 * Replaces the hand-built employee-manager tree with a seat-driven,
 * ninety.io-grade **visual** org chart:
 *
 *   - `d3-hierarchy` computes node positions (layout math only — see
 *     `seatLayout.ts`); React renders absolutely-positioned `SeatCard`s
 *     with SVG connector lines.
 *   - Zoom + pan (wheel + background drag; explicit zoom buttons too).
 *   - Toolbar: scenario toggle (Current / Future), department filter,
 *     Add seat, Print, Export PNG / PDF.
 *   - Drag a seat onto another → reparent (`PUT /:id {parentSeatId}`);
 *     a "drop to top" zone clears the parent.
 *   - A **bench** ("People without a seat") keeps the never-hide-anyone
 *     property from the old chart.
 *   - Vacant seats show a Vacant / Hiring badge + an assign control.
 *   - Admin-gated (visionary / integrator) for mutations; everyone views.
 *
 * The interactive chart is `print:hidden`; a compact nested-list mirror
 * renders for print + PDF pagination. Export PNG/PDF serialize the chart
 * to a standalone SVG (`buildChartSvg`).
 */

const GAP_X = 28;
const GAP_Y = 56;
const MIN_ZOOM = 0.3;
const MAX_ZOOM = 2;

export function AccountabilityChartPage() {
  const { state: authState } = useAuth();
  const queryClient = useQueryClient();
  const toast = useToast();

  const canManage =
    authState.status === 'authenticated' &&
    ['visionary', 'integrator'].includes(authState.me.membership.role);
  const orgName = authState.status === 'authenticated' ? authState.me.organization.name : 'Org';
  const generatedAt = useMemo(() => new Date(), []);

  // ── Toolbar state ──────────────────────────────────────────────────
  const [scenario, setScenario] = useState<SeatScenario>('current');
  const [departmentFilter, setDepartmentFilter] = useState<string>(''); // '' = full view

  // ── Selection / editing state ──────────────────────────────────────
  const [selectedSeatId, setSelectedSeatId] = useState<string | null>(null);
  const [editorState, setEditorState] = useState<{ mode: 'create' | 'edit'; seat?: SeatView } | null>(null);
  const [assignTarget, setAssignTarget] = useState<SeatView | null>(null);

  // ── Drag state ─────────────────────────────────────────────────────
  const [draggingSeatId, setDraggingSeatId] = useState<string | null>(null);
  const [dropTargetSeatId, setDropTargetSeatId] = useState<string | null>(null);

  // ── Zoom / pan ─────────────────────────────────────────────────────
  const [transform, setTransform] = useState({ x: 24, y: 16, k: 1 });
  const viewportRef = useRef<HTMLDivElement>(null);
  const panState = useRef<{ startX: number; startY: number; origX: number; origY: number } | null>(null);
  const [isPanning, setIsPanning] = useState(false);

  // ── Data ───────────────────────────────────────────────────────────
  const seatsQuery = useQuery({
    queryKey: ['seats', { scenario }],
    queryFn: () => listSeats({ scenario }),
  });
  const employeesQuery = useQuery({
    queryKey: ['employees', { status: 'all' as const, limit: 500 }],
    queryFn: () => listEmployees({ status: 'all', limit: 500 }),
  });
  const departmentsQuery = useQuery({ queryKey: ['departments'], queryFn: listDepartments });

  // GWC — best-effort. Leadership-only endpoints; a 403 for plain members
  // just means no indicators (never fabricate a verdict).
  const roundsQuery = useQuery({
    queryKey: ['eos', 'people-analyzer', 'rounds'],
    queryFn: listRounds,
    retry: false,
  });
  const latestClosedRound = useMemo(() => {
    const closed = (roundsQuery.data ?? []).filter((r) => r.status === 'closed' && r.closedAt);
    closed.sort((a, b) => (b.closedAt ?? '').localeCompare(a.closedAt ?? ''));
    return closed[0] ?? null;
  }, [roundsQuery.data]);
  const scoresQuery = useQuery({
    queryKey: ['eos', 'people-analyzer', 'scores', latestClosedRound?.id],
    queryFn: () => listRoundScores(latestClosedRound?.id ?? ''),
    enabled: latestClosedRound !== null,
    retry: false,
  });
  const gwcByMembership = useMemo(() => aggregateGwcByMembership(scoresQuery.data ?? []), [scoresQuery.data]);

  const seats = useMemo(() => seatsQuery.data ?? [], [seatsQuery.data]);
  const departments = useMemo(() => departmentsQuery.data ?? [], [departmentsQuery.data]);
  const deptName = useMemo(() => new Map(departments.map((d) => [d.id, d.name])), [departments]);

  const filteredSeats = useMemo(
    () => (departmentFilter ? seats.filter((s) => s.departmentId === departmentFilter) : seats),
    [seats, departmentFilter],
  );

  const seatTree = useMemo(() => buildSeatTree(filteredSeats), [filteredSeats]);
  const layout = useMemo<SeatLayout>(
    () =>
      layoutSeatTree(seatTree.roots, {
        nodeWidth: NODE_WIDTH,
        nodeHeight: NODE_HEIGHT,
        gapX: GAP_X,
        gapY: GAP_Y,
      }),
    [seatTree],
  );

  // The bench is computed against ALL scenario seats (not the department
  // filter) so someone seated in another department isn't wrongly benched.
  const activeEmployees = useMemo(
    () => (employeesQuery.data?.data ?? []).filter((e) => e.isActive),
    [employeesQuery.data],
  );
  const bench = useMemo(() => employeesWithoutSeat(activeEmployees, seats), [activeEmployees, seats]);

  const selectedSeat = useMemo(
    () => seats.find((s) => s.id === selectedSeatId) ?? null,
    [seats, selectedSeatId],
  );

  const vacantCount = seats.filter((s) => !s.holderEmployeeId).length;
  const hiringCount = seats.filter((s) => !s.holderEmployeeId && s.hiring).length;

  function gwcForSeat(seat: SeatView): SeatGwc | null {
    const mid = seat.holderEmployee?.membershipId;
    if (!mid) return null;
    return gwcByMembership.get(mid) ?? null;
  }

  // ── Mutations ──────────────────────────────────────────────────────
  function invalidateSeats(): void {
    void queryClient.invalidateQueries({ queryKey: ['seats'] });
    void queryClient.invalidateQueries({ queryKey: ['employees'] });
  }

  const reparentMutation = useMutation({
    mutationFn: ({ seatId, parentSeatId }: { seatId: string; parentSeatId: string | null }) =>
      updateSeat(seatId, { parentSeatId }),
    onSuccess: () => {
      invalidateSeats();
      toast.show('Seat moved', { variant: 'success' });
    },
    onError: (err) => {
      const code = errorCode(err);
      if (code === 'would_create_seat_cycle') {
        toast.show('That move would create a reporting loop.', { variant: 'warning' });
      } else {
        toast.show('Could not move the seat — try again.', { variant: 'danger' });
      }
    },
  });

  const unassignMutation = useMutation({
    mutationFn: (seatId: string) => unassignSeat(seatId),
    onSuccess: () => {
      invalidateSeats();
      toast.show('Seat vacated', { variant: 'success' });
    },
    onError: () => {
      toast.show('Could not unassign — try again.', { variant: 'danger' });
    },
  });

  const hiringMutation = useMutation({
    mutationFn: ({ seatId, hiring }: { seatId: string; hiring: boolean }) => setSeatHiring(seatId, hiring),
    onSuccess: () => {
      invalidateSeats();
    },
    onError: () => {
      toast.show('Could not update hiring status.', { variant: 'danger' });
    },
  });

  function handleReparent(seatId: string, parentSeatId: string | null): void {
    if (!canManage) return;
    if (seatId === parentSeatId) return;
    reparentMutation.mutate({ seatId, parentSeatId });
  }

  // ── Zoom / pan handlers ────────────────────────────────────────────
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return undefined;
    function onWheel(e: WheelEvent): void {
      e.preventDefault();
      setTransform((t) => {
        const next = t.k * (1 - e.deltaY * 0.0015);
        return { ...t, k: clamp(next, MIN_ZOOM, MAX_ZOOM) };
      });
    }
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      el.removeEventListener('wheel', onWheel);
    };
  }, []);

  const onPanMove = useCallback((e: PointerEvent) => {
    const p = panState.current;
    if (!p) return;
    setTransform((t) => ({ ...t, x: p.origX + (e.clientX - p.startX), y: p.origY + (e.clientY - p.startY) }));
  }, []);

  const onPanUp = useCallback(() => {
    panState.current = null;
    setIsPanning(false);
    window.removeEventListener('pointermove', onPanMove);
    window.removeEventListener('pointerup', onPanUp);
  }, [onPanMove]);

  function onViewportPointerDown(e: React.PointerEvent<HTMLDivElement>): void {
    // Only pan from the background — never when starting a card drag.
    if ((e.target as HTMLElement).closest('[data-seat-card="true"]')) return;
    panState.current = { startX: e.clientX, startY: e.clientY, origX: transform.x, origY: transform.y };
    setIsPanning(true);
    window.addEventListener('pointermove', onPanMove);
    window.addEventListener('pointerup', onPanUp);
  }

  function zoomBy(factor: number): void {
    setTransform((t) => ({ ...t, k: clamp(t.k * factor, MIN_ZOOM, MAX_ZOOM) }));
  }
  function resetView(): void {
    setTransform({ x: 24, y: 16, k: 1 });
  }

  // ── Export ─────────────────────────────────────────────────────────
  // Print + PNG/PDF ship now; a real server-minted shareable link is
  // backend-dependent (ADR-028 leaves it out of scope) — deliberate marker:
  // eslint-disable-next-line no-warning-comments
  // TODO(share-link): wire a shareable-link button once the backend mints one.
  const exportBaseName =
    orgName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'org';

  function exportPng(): void {
    const svg = buildChartSvg(layout, deptName);
    const blob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      const scale = 2;
      const canvas = document.createElement('canvas');
      canvas.width = Math.ceil(layout.width * scale);
      canvas.height = Math.ceil(layout.height * scale);
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0);
      }
      canvas.toBlob((png) => {
        if (png) triggerDownload(URL.createObjectURL(png), `${exportBaseName}-accountability-chart.png`);
        URL.revokeObjectURL(url);
      }, 'image/png');
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.show('Could not export PNG in this browser — try Print → Save as PDF.', { variant: 'danger' });
    };
    img.src = url;
  }

  function exportPdf(): void {
    const svg = buildChartSvg(layout, deptName);
    const w = window.open('', '_blank');
    if (!w) {
      toast.show('Allow pop-ups to export a PDF, or use Print.', { variant: 'warning' });
      return;
    }
    const html =
      `<!doctype html><html><head><title>${escapeXml(orgName)} — Accountability Chart</title>` +
      `<style>@page{size:landscape;margin:10mm}body{margin:0}svg{width:100%;height:auto}</style></head>` +
      `<body>${svg}<script>window.onload=function(){window.focus();window.print();};</script></body></html>`;
    // Blob navigation instead of the deprecated document.write.
    w.location.href = URL.createObjectURL(new Blob([html], { type: 'text/html' }));
  }

  // ── Render ─────────────────────────────────────────────────────────
  const isLoading = seatsQuery.isLoading || employeesQuery.isLoading;

  if (seatsQuery.error) {
    return (
      <Card className="flex items-start gap-3 border-kw-danger-text/30 bg-kw-danger-bg p-4">
        <AlertTriangle className="size-5 shrink-0 text-kw-danger-text" />
        <div>
          <p className="text-[14px] font-medium text-kw-danger-text">Couldn&apos;t load the chart</p>
          <p className="mt-1 text-[13px] text-kw-danger-text/80">
            {seatsQuery.error instanceof Error ? seatsQuery.error.message : 'Unknown error'}
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-col gap-3 print:hidden">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h1 className="typo-h1">Accountability Chart</h1>
            <p className="mt-1 text-[13px] text-kw-muted">
              {seats.length} seat{seats.length === 1 ? '' : 's'} · {vacantCount} vacant
              {hiringCount > 0 ? ` (${String(hiringCount)} hiring)` : ''} · {bench.length} on the bench
              {seatTree.brokenCycleEdges.length > 0
                ? ` · ${String(seatTree.brokenCycleEdges.length)} loop${
                    seatTree.brokenCycleEdges.length === 1 ? '' : 's'
                  } broken`
                : ''}
            </p>
          </div>
          <Link
            to="/employees"
            className="inline-flex items-center gap-1.5 rounded-kw-md border border-kw-border bg-white px-3 py-2 text-[13px] font-medium text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
          >
            <Users className="size-4" />
            Directory
          </Link>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Scenario toggle */}
          <div
            role="group"
            aria-label="Scenario"
            className="inline-flex overflow-hidden rounded-kw-md border border-kw-border"
          >
            {(['current', 'future'] as SeatScenario[]).map((s) => (
              <button
                key={s}
                type="button"
                aria-pressed={scenario === s}
                onClick={() => {
                  setScenario(s);
                }}
                className={`px-3 py-1.5 text-[13px] font-medium transition-colors ${
                  scenario === s ? 'bg-kw-cerulean text-white' : 'bg-white text-kw-shark hover:bg-kw-bg'
                }`}
              >
                {s === 'current' ? 'Current' : 'Future'}
              </button>
            ))}
          </div>

          {/* Department filter */}
          <select
            aria-label="Department filter"
            value={departmentFilter}
            onChange={(e) => {
              setDepartmentFilter(e.target.value);
            }}
            className="rounded-kw-md border border-kw-border bg-white px-2.5 py-1.5 text-[13px] text-kw-shark focus:border-kw-cerulean focus:outline-none focus:ring-2 focus:ring-kw-cerulean/20"
          >
            <option value="">Full view (all departments)</option>
            {departments.map((d) => (
              <option key={d.id} value={d.id}>
                {d.name}
              </option>
            ))}
          </select>

          <div className="ml-auto flex flex-wrap items-center gap-2">
            {canManage ? (
              <Button
                size="sm"
                onClick={() => {
                  setEditorState({ mode: 'create' });
                }}
              >
                <Plus className="size-4" />
                Add seat
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="secondary"
              onClick={() => {
                window.print();
              }}
            >
              <Printer className="size-4" />
              Print
            </Button>
            <Button size="sm" variant="secondary" onClick={exportPng}>
              <Download className="size-4" />
              PNG
            </Button>
            <Button size="sm" variant="secondary" onClick={exportPdf}>
              <FileText className="size-4" />
              PDF
            </Button>
          </div>
        </div>
      </div>

      {/* Print cover */}
      <header className="hidden space-y-1 border-b border-black pb-4 print:block">
        <p className="font-mono text-[11px] uppercase tracking-wide text-black">Accountability Chart</p>
        <h2 className="text-[18px] font-semibold text-black">{orgName}</h2>
        <p className="text-[12px] text-black">
          {scenario === 'future' ? 'Future / scenario view · ' : ''}Generated {generatedAt.toLocaleString()}
        </p>
      </header>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <div className="size-8 animate-spin rounded-full border-2 border-kw-cerulean border-t-transparent" />
        </div>
      ) : seats.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-12 text-center print:hidden">
          <Network className="size-10 text-kw-ghost" />
          <div>
            <p className="text-[15px] font-medium text-kw-shark">
              No seats on the {scenario === 'future' ? 'future' : 'current'} chart yet
            </p>
            <p className="mt-1 text-[13px] text-kw-muted">
              {canManage
                ? 'Add the first seat to start building your accountability chart.'
                : 'An admin can add the first seats.'}
            </p>
          </div>
          {canManage ? (
            <Button
              onClick={() => {
                setEditorState({ mode: 'create' });
              }}
            >
              <Plus className="size-4" />
              Add seat
            </Button>
          ) : null}
        </Card>
      ) : (
        <>
          {/* Drop-to-top zone (admins, while dragging) */}
          {canManage && draggingSeatId ? (
            <DropToTopZone
              onDropSeat={(id) => {
                handleReparent(id, null);
              }}
            />
          ) : null}

          {/* Interactive chart */}
          <div className="relative print:hidden">
            <div className="absolute right-3 top-3 z-10 flex flex-col gap-1">
              <ZoomButton
                label="Zoom in"
                onClick={() => {
                  zoomBy(1.2);
                }}
              >
                <Plus className="size-4" />
              </ZoomButton>
              <ZoomButton
                label="Zoom out"
                onClick={() => {
                  zoomBy(1 / 1.2);
                }}
              >
                <Minus className="size-4" />
              </ZoomButton>
              <ZoomButton label="Reset view" onClick={resetView}>
                <Maximize2 className="size-4" />
              </ZoomButton>
            </div>

            <div
              ref={viewportRef}
              onPointerDown={onViewportPointerDown}
              className={`relative h-[68vh] min-h-[420px] overflow-hidden rounded-kw-lg border border-kw-border bg-kw-bg ${
                isPanning ? 'cursor-grabbing' : 'cursor-grab'
              }`}
            >
              <div
                className="absolute left-0 top-0 origin-top-left"
                style={{
                  transform: `translate(${String(transform.x)}px, ${String(transform.y)}px) scale(${String(transform.k)})`,
                  width: layout.width,
                  height: layout.height,
                }}
              >
                {/* Connector lines */}
                <svg
                  width={layout.width}
                  height={layout.height}
                  className="pointer-events-none absolute left-0 top-0"
                  aria-hidden
                >
                  {layout.edges.map((edge) => (
                    <path
                      key={`${edge.parentId}-${edge.childId}`}
                      d={connectorPath(edge.x1, edge.y1, edge.x2, edge.y2)}
                      fill="none"
                      stroke="#cbd5e1"
                      strokeWidth={1.5}
                    />
                  ))}
                </svg>

                {/* Seat cards */}
                {layout.nodes.map((pos) => (
                  <div
                    key={pos.seat.id}
                    data-seat-card="true"
                    className="absolute"
                    style={{ left: pos.x, top: pos.y, width: NODE_WIDTH, height: NODE_HEIGHT }}
                  >
                    <SeatCard
                      seat={pos.seat}
                      departmentName={pos.seat.departmentId ? deptName.get(pos.seat.departmentId) : null}
                      gwc={gwcForSeat(pos.seat)}
                      canManage={canManage}
                      isDragging={draggingSeatId === pos.seat.id}
                      isDropTarget={dropTargetSeatId === pos.seat.id && draggingSeatId !== pos.seat.id}
                      style={{ width: NODE_WIDTH, height: NODE_HEIGHT }}
                      onSelect={() => {
                        setSelectedSeatId(pos.seat.id);
                      }}
                      onDragStartSeat={setDraggingSeatId}
                      onDragEndSeat={() => {
                        setDraggingSeatId(null);
                        setDropTargetSeatId(null);
                      }}
                      onDragOverSeat={setDropTargetSeatId}
                      onDragLeaveSeat={() => {
                        setDropTargetSeatId(null);
                      }}
                      onDropSeat={(dragged, target) => {
                        setDropTargetSeatId(null);
                        handleReparent(dragged, target);
                      }}
                    />
                  </div>
                ))}
              </div>
            </div>
            {canManage ? (
              <p className="mt-1.5 text-[12px] text-kw-muted">
                Drag a seat onto another to change who it reports into. Drag the background to pan; scroll to
                zoom.
              </p>
            ) : null}
          </div>

          {/* Print-only nested-list mirror (clean pagination) */}
          <div className="hidden print:block">
            <ol className="space-y-1">
              {seatTree.roots.map((root) => (
                <PrintSeatNode key={root.seat.id} node={root} deptName={deptName} />
              ))}
            </ol>
          </div>
        </>
      )}

      {/* Bench — people without a seat (never hide anyone) */}
      <section className="space-y-2">
        <h2 className="flex items-center gap-2 text-[12px] font-medium uppercase tracking-wide text-kw-muted">
          <Users className="size-3.5" />
          People without a seat <span className="font-normal text-kw-ghost">({bench.length})</span>
        </h2>
        {bench.length === 0 ? (
          <p className="text-[13px] text-kw-muted">Everyone active holds at least one seat.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {bench.map((e) => (
              <BenchCard key={e.id} employee={e} deptName={deptName} />
            ))}
          </div>
        )}
      </section>

      {/* Drawer + modals */}
      <SeatDrawer
        seat={selectedSeat}
        departmentName={selectedSeat?.departmentId ? deptName.get(selectedSeat.departmentId) : null}
        gwc={selectedSeat ? gwcForSeat(selectedSeat) : null}
        canManage={canManage}
        isMutating={unassignMutation.isPending || hiringMutation.isPending}
        onClose={() => {
          setSelectedSeatId(null);
        }}
        onEdit={(seat) => {
          setSelectedSeatId(null);
          setEditorState({ mode: 'edit', seat });
        }}
        onAssign={(seat) => {
          setSelectedSeatId(null);
          setAssignTarget(seat);
        }}
        onUnassign={(seat) => {
          unassignMutation.mutate(seat.id);
        }}
        onToggleHiring={(seat) => {
          hiringMutation.mutate({ seatId: seat.id, hiring: !seat.hiring });
        }}
      />

      {editorState ? (
        <SeatEditorModal
          open
          mode={editorState.mode}
          seat={editorState.seat}
          scenario={scenario}
          departments={departments}
          allSeats={seats}
          onClose={() => {
            setEditorState(null);
          }}
          onSaved={() => {
            invalidateSeats();
            toast.show(editorState.mode === 'edit' ? 'Seat updated' : 'Seat created', {
              variant: 'success',
            });
            setEditorState(null);
          }}
        />
      ) : null}

      <AssignSeatModal
        open={assignTarget !== null}
        seat={assignTarget}
        employees={activeEmployees}
        onClose={() => {
          setAssignTarget(null);
        }}
        onAssigned={() => {
          invalidateSeats();
          toast.show('Seat assigned', { variant: 'success' });
          setAssignTarget(null);
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────────────────────────────────

function ZoomButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="grid size-8 place-items-center rounded-kw-md border border-kw-border bg-white text-kw-shark shadow-kw-xs hover:border-kw-cerulean/40 hover:text-kw-cerulean"
    >
      {children}
    </button>
  );
}

function DropToTopZone({ onDropSeat }: { onDropSeat: (seatId: string) => void }) {
  const [isOver, setIsOver] = useState(false);
  return (
    <div
      onDragOver={(e) => {
        if (!Array.from(e.dataTransfer.types).includes(SEAT_DRAG_MIME)) return;
        e.preventDefault();
        setIsOver(true);
      }}
      onDragLeave={() => {
        setIsOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsOver(false);
        const id = e.dataTransfer.getData(SEAT_DRAG_MIME);
        if (id) onDropSeat(id);
      }}
      className={`flex items-center gap-2 rounded-kw-md border-2 border-dashed p-3 text-[13px] transition-colors print:hidden ${
        isOver
          ? 'border-kw-cerulean bg-kw-bg-blue text-kw-cerulean-dark'
          : 'border-kw-border bg-white text-kw-muted'
      }`}
    >
      <Maximize2 className="size-4" />
      Drop here to move this seat to the top of the chart.
    </div>
  );
}

function BenchCard({ employee, deptName }: { employee: EmployeeListItem; deptName: Map<string, string> }) {
  const initials = `${employee.firstName.charAt(0)}${employee.lastName.charAt(0)}`.toUpperCase() || '·';
  return (
    <Link
      to={`/employees/${employee.id}`}
      className="flex items-center gap-2 rounded-kw-lg border border-kw-border bg-white px-3 py-2 shadow-kw-xs hover:border-kw-cerulean/40"
    >
      <span className="grid size-8 shrink-0 place-items-center rounded-kw-full bg-kw-ghost/20 text-[12px] font-medium text-kw-shark">
        {initials}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-medium text-kw-shark">
          {employee.firstName} {employee.lastName}
        </span>
        <span className="block truncate text-[11px] text-kw-muted">
          {employee.jobTitle ??
            (employee.departmentId ? deptName.get(employee.departmentId) : null) ??
            'No seat yet'}
        </span>
      </span>
    </Link>
  );
}

function PrintSeatNode({ node, deptName }: { node: SeatTreeNode; deptName: Map<string, string> }) {
  const s = node.seat;
  const holder = s.holderEmployee;
  return (
    <li>
      <div className="flex items-baseline gap-2 text-[12px] text-black">
        <span className="font-semibold">{s.name}</span>
        <span>· {roleLabel(s.systemRole)}</span>
        <span>· {holder ? `${holder.firstName} ${holder.lastName}` : s.hiring ? 'Hiring' : 'Vacant'}</span>
        {s.departmentId ? <span>· {deptName.get(s.departmentId) ?? '—'}</span> : null}
      </div>
      {node.children.length > 0 ? (
        <ol className="ml-4 mt-0.5 space-y-0.5 border-l border-black/30 pl-3">
          {node.children.map((child) => (
            <PrintSeatNode key={child.seat.id} node={child} deptName={deptName} />
          ))}
        </ol>
      ) : null}
    </li>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}

/** Vertical-elbow Bézier connector from (x1,y1) to (x2,y2). */
function connectorPath(x1: number, y1: number, x2: number, y2: number): string {
  const midY = (y1 + y2) / 2;
  return `M ${String(x1)} ${String(y1)} C ${String(x1)} ${String(midY)}, ${String(x2)} ${String(midY)}, ${String(x2)} ${String(y2)}`;
}

function errorCode(err: unknown): string | null {
  if (err && typeof err === 'object' && 'response' in err) {
    const response = (err as { response?: { data?: { error?: unknown } } }).response;
    const code = response?.data?.error;
    if (typeof code === 'string') return code;
  }
  return null;
}

function triggerDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

function escapeXml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function truncate(s: string, max: number): string {
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * Serialize the laid-out chart to a standalone SVG string — the source for
 * both PNG (raster via canvas) and PDF (print window) export. Pure shapes +
 * text so the canvas is never tainted.
 */
function buildChartSvg(layout: SeatLayout, deptName: Map<string, string>): string {
  const edges = layout.edges
    .map(
      (e) =>
        `<path d="${connectorPath(e.x1, e.y1, e.x2, e.y2)}" fill="none" stroke="#cbd5e1" stroke-width="1.5"/>`,
    )
    .join('');

  const nodes = layout.nodes
    .map((pos) => {
      const s = pos.seat;
      const holder = s.holderEmployee;
      const holderLine = holder ? `${holder.firstName} ${holder.lastName}` : s.hiring ? 'Hiring' : 'Vacant';
      const dept = s.departmentId ? (deptName.get(s.departmentId) ?? '') : '';
      const meta = [roleLabel(s.systemRole), dept].filter(Boolean).join(' · ');
      return (
        `<g transform="translate(${String(pos.x)},${String(pos.y)})">` +
        `<rect width="${String(NODE_WIDTH)}" height="${String(NODE_HEIGHT)}" rx="10" fill="#ffffff" stroke="#e2e8f0"/>` +
        `<text x="14" y="26" font-family="Arial, sans-serif" font-size="14" font-weight="700" fill="#1e293b">${escapeXml(truncate(s.name, 30))}</text>` +
        `<text x="14" y="52" font-family="Arial, sans-serif" font-size="12" fill="${holder ? '#334155' : '#94a3b8'}">${escapeXml(truncate(holderLine, 32))}</text>` +
        `<text x="14" y="${String(NODE_HEIGHT - 16)}" font-family="Arial, sans-serif" font-size="11" fill="#64748b">${escapeXml(truncate(meta, 34))}</text>` +
        `</g>`
      );
    })
    .join('');

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" width="${String(layout.width)}" height="${String(layout.height)}" viewBox="0 0 ${String(layout.width)} ${String(layout.height)}">` +
    `<rect width="100%" height="100%" fill="#ffffff"/>${edges}${nodes}</svg>`
  );
}
